import React, { useContext, useEffect } from "react";
import { ProspectFiltersType } from "../containers/Prospects";
import { useMetadata } from "./MetadataContext";

export type ProspectGridSavedSearches = {
  currentYearRegistered: Partial<ProspectFiltersType>;
  profileCompletedLast24Hrs: Partial<ProspectFiltersType>;
  generalQuestionnaireLast24Hrs: Partial<ProspectFiltersType>;
  medicalQuestionnaireLast24Hrs: Partial<ProspectFiltersType>;
  clubQuestionnaireLast24Hrs: Partial<ProspectFiltersType>;
  previousSearch: Partial<ProspectFiltersType>;
};

type SavedSearchPages = {
  prospectsGrid: ProspectGridSavedSearches;
};

type SavedSearchKeys = keyof ProspectGridSavedSearches;
type SavedSearchValue = ProspectGridSavedSearches[keyof ProspectGridSavedSearches];

type SavedSearchContextType = {
  saveSearch: (page: keyof SavedSearchPages, searchKey: string, searchValue: string) => void;
  getSearch: <T extends SavedSearchValue>(page: keyof SavedSearchPages, searchKey: string) => T | null;
};

const SavedSearchContext = React.createContext<SavedSearchContextType | undefined>(undefined);

const SavedSearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { draftYears } = useMetadata();
  const draftYear: string = !!draftYears?.currentDraftYear ? draftYears?.currentDraftYear?.toString() : "";

  const saveSearch = (page: keyof SavedSearchPages, searchKey: SavedSearchKeys, searchValue: string) => {
    const existingSearchesStr = localStorage.getItem("savedSearches");
    if (existingSearchesStr === null) {
      localStorage.setItem("savedSearches", JSON.stringify({ [page]: { [searchKey]: searchValue } }));
    } else {
      const existingSearches = JSON.parse(existingSearchesStr);
      localStorage.setItem(
        "savedSearches",
        JSON.stringify({
          ...existingSearches,
          [page]: { ...existingSearches[page], [searchKey]: searchValue },
        }),
      );
    }
  };

  const saveProspectGridSearches = async (year: string) => {
    saveSearch(
      "prospectsGrid",
      "currentYearRegistered",
      JSON.stringify({
        draftYears: [{ value: parseInt(year), label: year }],
        invitationStatuses: [
          {
            value: {
              value: "registered",
              name: "Registered",
            },
            label: "Registered",
          },
        ],
      } as Partial<ProspectFiltersType>),
    );
    saveSearch(
      "prospectsGrid",
      "profileCompletedLast24Hrs",
      JSON.stringify({
        draftYears: [{ value: parseInt(year), label: year }],
        taskFilter: {
          taskAssigned: true,
          taskCompletedRange: "day",
          taskCompletedAfterDate: null,
          taskCompletedByDate: null,
          taskTypeIds: [4],
        },
      } as Partial<ProspectFiltersType>),
    );
    saveSearch(
      "prospectsGrid",
      "generalQuestionnaireLast24Hrs",
      JSON.stringify({
        draftYears: [{ value: parseInt(year), label: year }],
        taskFilter: {
          taskAssigned: true,
          taskCompletedRange: "day",
          taskCompletedAfterDate: null,
          taskCompletedByDate: null,
          taskTypeIds: [1],
        },
      } as Partial<ProspectFiltersType>),
    );
    saveSearch(
      "prospectsGrid",
      "medicalQuestionnaireLast24Hrs",
      JSON.stringify({
        draftYears: [{ value: parseInt(year), label: year }],
        taskFilter: {
          taskAssigned: true,
          taskCompletedRange: "day",
          taskCompletedAfterDate: null,
          taskCompletedByDate: null,
          taskTypeIds: [2],
        },
      } as Partial<ProspectFiltersType>),
    );
    saveSearch(
      "prospectsGrid",
      "clubQuestionnaireLast24Hrs",
      JSON.stringify({
        draftYears: [{ value: parseInt(year), label: year }],
        taskFilter: {
          taskAssigned: true,
          taskCompletedRange: "day",
          taskCompletedAfterDate: null,
          taskCompletedByDate: null,
          taskTypeIds: [3],
        },
      } as Partial<ProspectFiltersType>),
    );
  };

  useEffect(() => {
    if (draftYear) {
      saveProspectGridSearches(draftYear);
    }
  }, [draftYear]);

  const getSearch = <T extends SavedSearchValue>(
    page: keyof SavedSearchPages,
    searchKey: SavedSearchKeys,
  ): T | null => {
    const existingSearchesStr = localStorage.getItem("savedSearches");
    if (existingSearchesStr === null) {
      return null;
    }
    const pages = JSON.parse(existingSearchesStr);
    if (!pages[page][searchKey]) {
      return null;
    }
    return JSON.parse(pages[page][searchKey]) as T;
  };

  return <SavedSearchContext.Provider value={{ saveSearch, getSearch }}>{children}</SavedSearchContext.Provider>;
};

const useSavedSearches = (): SavedSearchContextType => {
  const context: SavedSearchContextType | undefined = useContext(SavedSearchContext);
  if (context === undefined) {
    throw new Error(`useSavedSearches must be used within a SavedSearchProvider`);
  }
  return context;
};

export { SavedSearchProvider, useSavedSearches };
